// src/components/matricula/FormRematricula.tsx

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { InputMask } from 'primereact/inputmask';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { Printd } from 'printd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../api/api';
import { cpf } from 'cpf-cnpj-validator';
import logo from '../assets/logo_conservatorio.jpg';

import '../layout/sass/_form.scss';

export const FormRematricula = () => {

  const toast = useRef(null);
  const printd = new Printd();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [matriculasAnoAtual, setMatriculasAnoAtual] = useState([]);
  const [teoriaAnterior, setTeoriaAnterior] = useState(null);
  const [instrumentoAnterior, setInstrumentoAnterior] = useState(null);
  const [listTurmaTeoria, setListTurmaTeoria] = useState([]);
  const [listTurmaInstrumento, setListTurmaInstrumento] = useState([]);
  const [turmaTeoriaSelected, setTurmaTeoriaSelected] = useState(null);
  const [turmaInstrumentoSelected, setTurmaInstrumentoSelected] = useState(null);
  const [matriculaGerada, setMatriculaGerada] = useState(null);
  const [dadosComprovante, setDadosComprovante] = useState(null); // Estado para comprovante

  const [showJaRematriculado, setShowJaRematriculado] = useState(false);
  const [showSemMatriculaAnterior, setShowSemMatriculaAnterior] = useState(false);
  const [showTurmaCheia, setShowTurmaCheia] = useState(false);
  const [showComprovante, setShowComprovante] = useState(false);

  const anoAtual = new Date().getFullYear();
  const notaCorteTeoria = 60;

  const STATUS_VALIDOS_REM = ['MATRICULADO', 'REMATRICULADO', 'TRANCADO'];

  // Dentro do FormRematricula
  const [periodoRematricula, setPeriodoRematricula] = useState(null);

  const cssPrint = `
    #print-area { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 20px; }
    .field { margin: 12px 0; font-size: 1.05rem; }
    img { max-width: 220px; }
    h3 { color: #2c3e50; }
  `;

  const validationSchema = Yup.object().shape({
    alunoId: Yup.number().required('Aluno é obrigatório'),
    cpf: Yup.string().required('CPF é obrigatório'),
  });

  useEffect(() => {
    const fetchPeriodo = async () => {
      try {
        const response = await api.get('periodoMatricula/periodoAtual');
        setPeriodoRematricula(response.data);
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Erro ao carregar período',
          detail: err.message,
        });
      }
    };
    fetchPeriodo();
  }, []);

  const formik = useFormik({
    initialValues: {
      cpf: '',
      alunoId: '',
    },
    validationSchema,
    onSubmit: async () => {
      if (!perfil) return;

      setIsSubmitting(true);

      try {
        // Dentro do onSubmit, substitua o bloco de criação das matrículas por:

        const matriculasToCreate = [];

        // Sempre cria a matrícula de teoria (se selecionada)
        if (turmaTeoriaSelected) {
          const matTeoria = {
            turmaId: turmaTeoriaSelected,
            alunoId: perfil.id,
            periodoId: periodoRematricula?.id,
            status: 'REMATRICULADO',
            dataMatricula: new Date(),
            conclusao: `${anoAtual}-12-31`,
            notaFinal: 0,
          };

          // Se selecionou instrumento (continuidade OU espera)
          if (turmaInstrumentoSelected) {
            const isListaEspera = listTurmaInstrumento.some(opt => opt.value === turmaInstrumentoSelected && !opt.label.includes(':')); // heurística: opções de espera não têm "dia/hora"

            if (isListaEspera) {
              matTeoria.instrumentoId = turmaInstrumentoSelected; // ID do CURSO
            } else {
              matTeoria.instrumentoId = turmaInstrumentoSelected; // Se continuidade, pode usar o mesmo (se backend permitir)
            }
          }

          matriculasToCreate.push(matTeoria);
        }

        // Se NÃO selecionou teoria, mas selecionou instrumento da espera → cria matrícula só com instrumentoId
        else if (turmaInstrumentoSelected && listTurmaInstrumento.some(opt => opt.value === turmaInstrumentoSelected && !opt.label.includes(':'))) {
          matriculasToCreate.push({
            alunoId: perfil.id,
            periodoId: periodoRematricula?.id,
            instrumentoId: turmaInstrumentoSelected, // ID do CURSO
            status: 'REMATRICULADO',
            dataMatricula: new Date(),
            conclusao: `${anoAtual}-12-31`,
            notaFinal: 0,
          });
        }

        // ... resto do loop de verificação de vagas (só para quem tem turmaId) e post
        for (const mat of matriculasToCreate) {
          const turmaRes = await api.get(`/turma/${mat.turmaId}`);
          const inscritosRes = await api.get(`/matricula/inscritos/${mat.turmaId}`);

          const qtdeInscritos = Number(inscritosRes.data ?? 0);
          const qtdeVagas = Number(turmaRes.data?.qtdeVagas ?? 0);

          if (qtdeInscritos >= qtdeVagas) {
            toast.current?.show({
              severity: 'error',
              summary: 'Turma lotada',
              detail: `A turma selecionada (${turmaRes.data?.perfilNome || '—'}) está cheia.`,
              life: 6000,
            });
            setShowTurmaCheia(true);
            continue;
          }

          await api.post('/matricula', mat);
        }

        // Busca matrícula mais recente
        const matriculasRes = await api.get(`/matricula/cpf/${perfil.cpf}`);
        if (matriculasRes.data?.length > 0) {
          const ultima = matriculasRes.data.sort((a, b) =>
            new Date(b.dataMatricula) - new Date(a.dataMatricula)
          )[0];
          setMatriculaGerada(ultima);

          // Guarda dados para comprovante ANTES de limpar perfil
          setDadosComprovante({
            nome: perfil?.nome || '—',
            cpf: perfil?.cpf || '—',
            data: new Date().toLocaleString('pt-BR'),
            codigo: ultima?.id || '—',
            turmaTeoria: turmaTeoriaSelected
              ? listTurmaTeoria.find(t => t.value === turmaTeoriaSelected)?.label || '—'
              : 'Não selecionada / concluída',
            turmaInstrumento: turmaInstrumentoSelected
              ? listTurmaInstrumento.find(t => t.value === turmaInstrumentoSelected)?.label || '—'
              : 'Não selecionada',
          });

          confirmDialog({
            message: 'Rematrícula realizada com sucesso! Deseja visualizar/imprimir o comprovante?',
            header: 'Rematrícula Concluída',
            icon: 'pi pi-check-circle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => setShowComprovante(true),
          });
        }

        // Limpeza final
        formik.resetForm();
        setPerfil(null);
        setMatriculasAnoAtual([]);
        setTeoriaAnterior(null);
        setInstrumentoAnterior(null);
        setListTurmaTeoria([]);
        setListTurmaInstrumento([]);
        setTurmaTeoriaSelected(null);
        setTurmaInstrumentoSelected(null);

      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Erro ao rematricular',
          detail: err?.response?.data?.message || err.message,
          life: 7000,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const isInvalid = (field) => !!(formik.touched[field] && formik.errors[field]);

  const handleCpfComplete = useCallback(async (e) => {
    const cpfValue = e.value.trim();
    if (!cpf.isValid(cpfValue)) {
      toast.current?.show({ severity: 'warn', summary: 'CPF inválido' });
      return;
    }

    try {
      // 1. Busca perfil
      const perfilRes = await api.get(`/perfil/cpf/${cpfValue}`);
      if (!perfilRes?.data) {
        toast.current?.show({ severity: 'warn', summary: 'Perfil não encontrado' });
        return;
      }
      const perfilData = perfilRes.data;
      setPerfil(perfilData);
      formik.setFieldValue('alunoId', perfilData.id);
      formik.setFieldValue('cpf', cpfValue);

      // 2. Busca matrículas
      const matriculasRes = await api.get(`/matricula/cpf/${cpfValue}`);
      const matriculas = matriculasRes.data || [];

      // 3. Matrícula no ano atual? → bloqueia
      const matAnoAtual = matriculas.filter(m => m.turma?.ano === anoAtual);
      setMatriculasAnoAtual(matAnoAtual);

      if (matAnoAtual.length > 0) {
        setShowJaRematriculado(true);
        setPerfil(null);
        return;
      }

      // 4. Matrículas no ano anterior válidas?

      const matAnoAnterior = matriculas.filter(m => m.turma?.ano === anoAtual - 1);
      const matAnoAnteriorValidas = matAnoAnterior.filter(m =>
        STATUS_VALIDOS_REM.includes(m.status) && m.status !== 'DESISTENTE'
      );

      if (matAnoAnteriorValidas.length === 0) {
        setShowSemMatriculaAnterior(true);
        setPerfil(null);
        return;
      }

      // 5. Separa teoria e instrumento
      const teorias = matAnoAnteriorValidas
        .filter(m =>
          m.turma?.curso?.tipo?.livre === false
        )
        .sort((a, b) => new Date(b.dataMatricula) - new Date(a.dataMatricula));

      const instrumentos = matAnoAnteriorValidas
        .filter(m =>
          m.turma?.curso?.tipo?.livre === true
        )
        .sort((a, b) => new Date(b.dataMatricula) - new Date(a.dataMatricula));

      const teoriaPrincipal = teorias[0] || null;
      const instrumentoPrincipal = instrumentos[0] || null;

      setTeoriaAnterior(teoriaPrincipal);
      setInstrumentoAnterior(instrumentoPrincipal);

      // 6. Carrega turmas de TEORIA
      let teoriaOferecida = false;



      if (teoriaPrincipal) {
        let cursoIdParaBuscar = teoriaPrincipal.turma.curso.id;
        let ehPosterior = false;

        // Vamos tentar oferecer teoria
        let deveOferecerTeoria = true;

        // 1. Se nota suficiente → tenta posterior
        if (teoriaPrincipal.notaFinal >= notaCorteTeoria) {
          try {
            const posteriorRes = await api.get(`/curso/posterior/${cursoIdParaBuscar}`);
            const posterior = posteriorRes.data;

            if (posterior?.id) {
              cursoIdParaBuscar = posterior.id;
              ehPosterior = true;
              toast.current?.show({
                severity: 'info',
                summary: 'Avanço de nível',
                detail: 'Você está apto a avançar para o próximo nível de teoria.',
                life: 4000,
              });
            } else {
              // Posterior NÃO existe → teoria concluída (não oferece nada)
              deveOferecerTeoria = false;
              toast.current?.show({
                severity: 'info',
                summary: 'Teoria concluída',
                detail: 'Você já concluiu todo o ciclo de teoria disponível.',
                life: 5000,
              });
            }
          } catch (err) {
            // Erro ao buscar posterior (ex: 404) → teoria concluída
            deveOferecerTeoria = false;
            console.warn('Curso posterior não encontrado:', err);
            toast.current?.show({
              severity: 'info',
              summary: 'Teoria concluída',
              detail: 'Não foi identificado nível posterior disponível.',
              life: 5000,
            });
          }
        } else {
          // Nota insuficiente → repete o mesmo curso
          toast.current?.show({
            severity: 'info',
            summary: 'Repetição de teoria',
            detail: 'Você irá repetir o mesmo nível de teoria.',
            life: 5000,
          });
          // cursoIdParaBuscar continua sendo o atual (repetição)
        }

        // 2. Só busca turmas se decidimos oferecer (posterior ou repetição)
        if (deveOferecerTeoria) {
          try {
            const turmasRes = await api.get(`/turma/comVagas/${cursoIdParaBuscar}/${anoAtual}`);
            const turmas = turmasRes.data || [];

            if (turmas.length > 0) {
              // Tem turmas → oferece no dropdown
              setListTurmaTeoria(
                turmas.map(t => ({
                  label: `${t.perfilNome || '—'} - ${t.cursoNome} - ${t.diaSemana} ${t.hora}`,
                  value: t.id,
                }))
              );
              // console.log('Turmas de teoria carregadas:', turmas);
            } else {
              // Sem vagas
              deveOferecerTeoria = false;
              toast.current?.show({
                severity: 'info',
                summary: 'Sem vagas disponíveis',
                detail: ehPosterior
                  ? 'Não há vagas no próximo nível de teoria.'
                  : 'Não há vagas para repetir o nível atual de teoria. Consulte a coordenação.',
                life: 6000,
              });
              setListTurmaTeoria([]);
            }
          } catch (err) {
            deveOferecerTeoria = false;
            console.error('Erro ao buscar turmas de teoria:', err);
            toast.current?.show({
              severity: 'error',
              summary: 'Erro ao carregar turmas',
              detail: 'Não foi possível carregar opções de teoria. Consulte a coordenação.',
              life: 7000,
            });
            setListTurmaTeoria([]);
          }
        } else {
          // Teoria concluída ou sem necessidade → limpa
          setListTurmaTeoria([]);
        }
      }

      // 7. Carrega turmas de INSTRUMENTO (continuidade sempre oferecida)
      if (instrumentoPrincipal?.turma?.curso?.id) {
        // Tem instrumento anterior → continuidade (turmas reais)
        const turmasInstRes = await api.get(`/turma/comVagas/${instrumentoPrincipal.turma.curso.id}/${anoAtual}`);
        const turmasInstrumento = turmasInstRes.data || [];

        setListTurmaInstrumento(
          turmasInstrumento.map(t => ({
            label: `${t.perfilNome || '—'} - ${t.cursoNome} - ${t.diaSemana} ${t.hora}`,
            value: t.id,
          }))
        );

        if (turmasInstrumento.length === 0) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Instrumento sem vagas',
            detail: 'A turma anterior de instrumento está lotada. Consulte a coordenação.',
            life: 6000,
          });
        }
      } else {
        // NÃO tem instrumento anterior → mostra lista de espera (cursos)
        try {
          const esperaRes = await api.get(`/curso/instrumentoEspera`);
          const cursosEspera = esperaRes.data || [];

          setListTurmaInstrumento(
            cursosEspera.map(c => ({
              label: c.nome || '—',  // ou c.descricao, dependendo do retorno
              value: c.id,           // id do CURSO
            }))
          );

          if (cursosEspera.length === 0) {
            toast.current?.show({
              severity: 'info',
              summary: 'Sem instrumentos disponíveis',
              detail: 'Não há instrumentos em lista de espera no momento.',
              life: 5000,
            });
          } else {
            toast.current?.show({
              severity: 'info',
              summary: 'Escolha um instrumento',
              detail: 'Selecione o instrumento desejado (em lista de espera).',
              life: 4000,
            });
          }
        } catch (err) {
          console.error('Erro ao carregar lista de espera de instrumentos:', err);
          toast.current?.show({
            severity: 'error',
            summary: 'Erro ao carregar instrumentos',
            detail: 'Não foi possível carregar a lista de espera. Consulte a coordenação.',
            life: 7000,
          });
          setListTurmaInstrumento([]);
        }
      }

    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro ao consultar histórico',
        detail: err?.response?.data?.message || err.message,
      });
    }
  }, [formik]);

  const handlePrint = () => {
    const el = document.getElementById('comprovante-rematricula');
    if (el) printd.print(el, [cssPrint]);
  };

  return (
    <div className="card">
      <Toast ref={toast} position="bottom-right" />

      <h2>FORMULÁRIO DE REMATRÍCULA – {anoAtual}</h2>
      <p>
        Período vigente: <strong>
          {new Date(periodoRematricula?.dataInicial).toLocaleDateString('pt-BR')} até{' '}
          {new Date(periodoRematricula?.dataFinal).toLocaleDateString('pt-BR')}
        </strong>
      </p>

      <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-4">
        <div className="p-field">
          <label htmlFor="cpf">CPF do aluno *</label>
          <InputMask
            id="cpf"
            mask="999.999.999-99"
            unmask
            value={formik.values.cpf}
            onChange={formik.handleChange}
            onComplete={handleCpfComplete}
            className={classNames({ 'p-invalid': isInvalid('cpf') })}
          />
        </div>

        <div className="p-field">
          <label>Aluno</label>
          <input
            type="text"
            value={perfil?.nome || ''}
            disabled
            className="p-inputtext p-component p-disabled"
          />
        </div>

        {listTurmaTeoria.length > 0 && (
          <div className="p-field">
            <label>Turma de Teoria (continuidade ou mesmo nível)</label>
            <Dropdown
              value={turmaTeoriaSelected}
              options={listTurmaTeoria}
              onChange={(e) => setTurmaTeoriaSelected(e.value)}
              placeholder="Selecione a turma de teoria"
              filter
              showClear
            />
          </div>
        )}

        {listTurmaInstrumento.length > 0 && (
          <div className="p-field">
            <label>Turma de Instrumento (continuidade)</label>
            <Dropdown
              value={turmaInstrumentoSelected}
              options={listTurmaInstrumento}
              onChange={(e) => setTurmaInstrumentoSelected(e.value)}
              placeholder="Selecione a turma de instrumento"
              filter
              showClear
            />
          </div>
        )}

        <div className="p-mt-6">
          <Button
            type="submit"
            label="Confirmar Rematrícula"
            icon="pi pi-refresh"
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              !perfil ||
              (listTurmaTeoria.length > 0 && !turmaTeoriaSelected) ||
              (listTurmaInstrumento.length > 0 && !turmaInstrumentoSelected)
            }
            className="p-button-primary"
          />
        </div>
      </form>

      {/* Diálogos de feedback */}
      <Dialog visible={showSemMatriculaAnterior} header="Atenção" onHide={() => setShowSemMatriculaAnterior(false)}>
        <p>Não foi encontrada matrícula anterior válida para este CPF.</p>
        <p>Este formulário é exclusivo para rematrícula. Utilize o formulário de matrícula nova quando disponível.</p>
      </Dialog>

      <Dialog visible={showJaRematriculado} header="Atenção" onHide={() => setShowJaRematriculado(false)}>
        <p>Já existe matrícula ou rematrícula registrada para este CPF no ano atual.</p>
      </Dialog>

      <Dialog visible={showTurmaCheia} header="Turma lotada" onHide={() => setShowTurmaCheia(false)}>
        <p>A turma selecionada não possui mais vagas disponíveis.</p>
      </Dialog>

      {/* Comprovante */}
      <Dialog
        visible={showComprovante}
        header="Comprovante de Rematrícula"
        onHide={() => setShowComprovante(false)}
        footer={<Button label="Imprimir" icon="pi pi-print" onClick={handlePrint} />}
      >
        <div id="comprovante-rematricula">
          <div className="header">
            <img src={logo} alt="Conservatório" />
            <h3>Comprovante de Rematrícula – {anoAtual}</h3>
          </div>

          <div className="field"><strong>Aluno:</strong> {dadosComprovante?.nome || '—'}</div>
          <div className="field"><strong>CPF:</strong> {dadosComprovante?.cpf || '—'}</div>
          <div className="field"><strong>Data da rematrícula:</strong> {dadosComprovante?.data || '—'}</div>

          <div className="field">
            <strong>Turma de Teoria:</strong> {dadosComprovante?.turmaTeoria || 'Não selecionada / concluída'}
          </div>

          <div className="field">
            <strong>Turma de Instrumento:</strong> {dadosComprovante?.turmaInstrumento || 'Não selecionada'}
          </div>

          <div className="field"><strong>Código(s) gerado(s):</strong> {dadosComprovante?.codigo || '—'}</div>
        </div>
      </Dialog>
    </div>
  );
};
